/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useContractAttachmentDeleteByIdMutation, useContractAttachmentSearchMutation, useContractGetByIdMutation, useContractUpdateByIdMutation} from '../Contract.api'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import {IContractDto, IContractFilter, IContractUpdateRequestDto} from '../Contract.model'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { array, object, string } from 'yup'
import { DateInputPicker } from '../../../common/component/DateInputPicker'
import { DOCUMENT_TYPES, FREQUENCIES, FREQUENCY, renderIdentifier, SERVICE_TYPES } from '../../../util/Constants'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../common/component/FilePicker'
import { IContractAttachmentDto } from '../ContractAttachment.model'
import { IAttachment } from '../../media/Media.model'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../contact/Contact.model'
import { ContactEditor } from '../../contact/components/ContactEditor'
import FormValidator from '../../../util/FormValidator.util'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'

interface IUpdateFormikSchema {
  clientId: string
  id: string
  documentTypeCode: string
  documentType: string
  serviceTypeCode: string
  serviceType: string
  serviceFrequencyCode: string
  serviceFrequency: string
  startDate: string
  endDate: string
  serviceAgreement: string
  serviceLocation: string
  serviceResponseTime: string
  comments?: string
  sellers: IContactDto[]
  contacts: IContactDto[]
  attachments: IAttachment[]
}

const ContractEditPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const {contractId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CONTRACTS'}),
      path: '/contracts',
      isSeparator: false,
      isActive: false,
    },
  ]
  
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      startDate: formValidator.validateStringStrict(),
      endDate: formValidator.validateStringStrict(),
      documentTypeCode: formValidator.validateStringStrict(),
      documentType: string().when('documentTypeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      serviceTypeCode: formValidator.validateStringStrict(),
      serviceType: string().when('serviceTypeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      serviceFrequencyCode: formValidator.validateStringStrict(),
      serviceFrequency: string().when('serviceFrequencyCode', {
        is: (value) => value === FREQUENCY.OTHER,
        then: () => formValidator.validateStringStrict(),
      }),
      serviceResponseTime: formValidator.validateString(),
      serviceAgreement: formValidator.validateString(2096),
      serviceLocation: formValidator.validateString(),
      comments: formValidator.validateString(2096),
      contacts: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      sellers: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
    clientId: '',
    id: `${contractId}`,
    documentTypeCode: '',
    documentType: '',
    serviceTypeCode: '',
    serviceType: '',
    serviceFrequencyCode: '',
    serviceFrequency: '',
    startDate: '',
    endDate: '',
    serviceResponseTime: '',
    serviceAgreement: '',
    serviceLocation: '',
    comments: '',
    contacts: [],
    sellers: [],
    attachments: [],
  })

  const didFetchContract = useRef(false)
  const [isContractLoading, setIsContractLoading] = useState(true)
  const [contract, setContract] = useState<IContractDto>()
  const [contractGetById] = useContractGetByIdMutation()
  const [contractUpdateById] = useContractUpdateByIdMutation()

  const didFetchAttachments = useRef(false)
  const didAttachmentRequestDelete = useRef(false)
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false)
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)
  const [contractAttachmentSearch] = useContractAttachmentSearchMutation()
  const [contractAttachmentDeleteById] = useContractAttachmentDeleteByIdMutation()

  const formik = useFormik<IUpdateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IContractUpdateRequestDto = {
            clientId: values.clientId,
            id: values.id,
            documentTypeCode: values.documentTypeCode,
            documentType: intl.formatMessage({
              id: `DROPDOWN.DOCUMENT_TYPE.${values.documentTypeCode}`,
            }),
            serviceTypeCode: values.serviceTypeCode,
            serviceType: intl.formatMessage({
              id: `DROPDOWN.SERVICE_TYPE.${values.serviceTypeCode}`,
            }),
            serviceResponseTime: values.serviceResponseTime,
            serviceFrequencyCode: values.serviceFrequencyCode,
            serviceFrequency:
              values.serviceFrequencyCode === FREQUENCY.OTHER
                ? values.serviceFrequency.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.FREQUENCY.${values.serviceFrequencyCode}`}),
            serviceAgreement: values.serviceAgreement,
            serviceLocation: values.serviceLocation,
            startDate: values.startDate,
            endDate: values.endDate,
            comments: values.comments ?? '',
            contacts: values.contacts,
            sellers: values.sellers,
            attachments: values.attachments,
          }

          const response: any = await contractUpdateById(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/contracts/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchContract = async (contractId) => {
      try {
        if (!didFetchContract.current) {
          setIsContractLoading(true)
          const response: any = await contractGetById({id: contractId})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const contract: IContractDto = response.data
            setContract(contract)

            formik.setFieldValue('clientId', contract.client.id)
            formik.setFieldValue('startDate', contract.startDate)
            formik.setFieldValue('endDate', contract.endDate)
            formik.setFieldValue('documentTypeCode', contract.documentTypeCode)
            formik.setFieldValue('documentType', contract.documentType)
            formik.setFieldValue('serviceTypeCode', contract.serviceTypeCode)
            formik.setFieldValue('serviceType', contract.serviceType)
            formik.setFieldValue('serviceFrequencyCode', contract.serviceFrequencyCode)
            formik.setFieldValue('serviceFrequency', contract.serviceFrequency)
            formik.setFieldValue('serviceResponseTime', contract.serviceResponseTime)
            formik.setFieldValue('serviceAgreement', contract.serviceAgreement)
            formik.setFieldValue('serviceLocation', contract.serviceLocation)
            formik.setFieldValue('comments', contract.comments)
            formik.setFieldValue('contacts', contract.contacts)
            formik.setFieldValue('sellers', contract.sellers)

            searchAttachment(contract.id)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsContractLoading(false)
      }
    }

    const searchAttachment = async (contractId) => {
      try {
        if (!didFetchAttachments.current) {
          setIsAttachmentsLoading(true)
          const response: any = await contractAttachmentSearch({
            filters: [{id: IContractFilter.HASH, value: contractId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const attachments: IContractAttachmentDto[] = response.data.data
            formik.setFieldValue(
              'attachments',
              attachments?.map((attachment) => {
                const file: IAttachment = {
                  id: attachment.id,
                  fileName: attachment.fileName,
                  temporalPath: attachment.temporalPath,
                  path: attachment.path,
                  size: attachment.size,
                  contentType: attachment.contentType,
                  comments: attachment.comments,
                  status: attachment.status,
                  processing: false,
                  uploaded: true,
                }
                return file
              })
            )
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAttachmentsLoading(false)
      }
    }

    if (contractId) {
      fetchContract(contractId)
    }
  }, [])

  const deleteAttachmentOnClickHandler = async (id: string) => {
    try {
      if (id && !didAttachmentRequestDelete.current) {
        const response: any = await contractAttachmentDeleteById(id)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          formik.setFieldValue('attachments', formik.values.attachments.filter(attachment => attachment.id !== id))
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    }
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.id && formik.errors.id) ||
          (formik.touched.documentTypeCode && formik.errors.documentTypeCode) ||
          (formik.touched.serviceTypeCode && formik.errors.serviceTypeCode) ||
          (formik.touched.serviceFrequencyCode && formik.errors.serviceFrequencyCode) ||
          (formik.touched.serviceFrequency && formik.errors.serviceFrequency) ||
          (formik.touched.startDate && formik.errors.startDate) ||
          (formik.touched.endDate && formik.errors.endDate) ||
          (formik.touched.serviceResponseTime && formik.errors.serviceResponseTime) ||
          (formik.touched.serviceAgreement && formik.errors.serviceAgreement) ||
          (formik.touched.comments && formik.errors.comments)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.contacts && formik.errors.contacts
      }
      case 'kt_tab_pane_3': {
        return formik.touched.sellers && formik.errors.sellers
      }
      case 'kt_tab_pane_4': {
        return formik.touched.attachments && formik.errors.attachments
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {contract && (
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fs-2 fw-bolder'>
                            {`${contract?.client?.displayName} - ${contract?.documentType}`}
                          </span>
                          <span className='fw-bolder text-primary me-1 fs-5'>
                            {renderIdentifier(contract.sequence)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      {contract && (
                        <>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary me-2 d-none d-md-block'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <>
                                <i className='bi bi-floppy'></i>
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'UTIL.SAVE'})}
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <i className='bi bi-floppy'></i>
                            )}
                          </button>
                        </>
                      )}

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/contracts/${contractId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isContractLoading && !formik.isSubmitting ? (
                <Spinner />
              ) : !contract ? (
                <div className='row'>
                  <div className='d-flex flex-row-fluid flex-start mt-10'>
                    <span className='text-danger '>
                      <div className='d-flex flex-row justify-content-start align-items-center'>
                        <span className='fs-6 me-1'>
                          <i className='text-danger me-1 bi bi-exclamation-octagon' />
                          {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {tabWithError('kt_tab_pane_1') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                        {tabWithError('kt_tab_pane_2') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                        {tabWithError('kt_tab_pane_3') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.SELLERS'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                        {tabWithError('kt_tab_pane_4') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                      </a>
                    </li>
                  </ul>
                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <ClientEmbedered client={contract.client} />
                        </div>
                      </div>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <DateInputPicker
                              label={intl.formatMessage({id: 'UTIL.DATE_START'})}
                              placeholder={intl.formatMessage({id: 'UTIL.DATE_START'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='startDate'
                            ></DateInputPicker>

                            <DateInputPicker
                              label={intl.formatMessage({id: 'UTIL.DATE_END'})}
                              placeholder={intl.formatMessage({id: 'UTIL.DATE_END'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='endDate'
                            ></DateInputPicker>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.DOCUMENT_TYPE'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='documentTypeCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('documentTypeCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {DOCUMENT_TYPES &&
                                    DOCUMENT_TYPES.map((documentType, index) => (
                                      <option key={index} value={documentType}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.DOCUMENT_TYPE.${documentType}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_TYPE'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='serviceTypeCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('serviceTypeCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {SERVICE_TYPES &&
                                    SERVICE_TYPES.map((serviceType, index) => (
                                      <option key={index} value={serviceType}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.SERVICE_TYPE.${serviceType}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='serviceFrequencyCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('serviceFrequencyCode')}
                                  onChange={(e) => {
                                    if (e.target.value === FREQUENCY.OTHER) {
                                      formik.setFieldValue('serviceFrequency', '')
                                    }
                                    formik.handleChange(e)
                                  }}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {FREQUENCIES.map((frequency, index) => (
                                    <option key={index} value={frequency}>
                                      {intl.formatMessage({
                                        id: `DROPDOWN.FREQUENCY.${frequency}`,
                                      })}
                                    </option>
                                  ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            {formik.values.serviceFrequencyCode === FREQUENCY.OTHER && (
                              <CustomInputRenderer
                                placeholder={intl.formatMessage({id: 'UTIL.SPECIFY'})}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName={`serviceFrequency`}
                                enableInputLength={true}
                              ></CustomInputRenderer>
                            )}

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                              placeholder={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`serviceLocation`}
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.RESPONSE_TIME'})}
                              placeholder={intl.formatMessage({id: 'UTIL.RESPONSE_TIME'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='serviceResponseTime'
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_AGREEMENT'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='serviceAgreement'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'UTIL.SERVICE_AGREEMENT'})}
                                  {...formik.getFieldProps('serviceAgreement')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.COMMENTS'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='comments'
                              object={
                                <textarea
                                  rows={4}
                                  className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                  placeholder={intl.formatMessage({id: 'UTIL.COMMENTS'})}
                                  {...formik.getFieldProps('comments')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                />
                              }
                              enableInputLength={true}
                              inputLength={3000}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor
                              formik={formik}
                              fieldName='contacts'
                              disabled={formik.isSubmitting}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor
                              formik={formik}
                              fieldName='sellers'
                              disabled={formik.isSubmitting}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                      <div className='row mb-5'>
                        <FilePicker
                          disabled={formik.isSubmitting}
                          formik={formik}
                          fieldName='attachments'
                          loading={isAttachmentsLoading}
                          fileDeleteOnClickHandler={deleteAttachmentOnClickHandler}
                          setIsAttachmentProcessing={setIsAttachmentProcessing}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

export {ContractEditPage}
